<script lang="ts">
  import { inertia } from "@inertiajs/svelte"
  import { _, locale } from "svelte-i18n"

  import LanguageSwitcher from "@/components/language-switcher.svelte"
  import SocialLinks from "@/components/social-links.svelte"
  import ThemeSwitcher from "@/components/theme-switcher.svelte"
  import TobyIcon from "@/components/toby-icon.svelte"
  import { BRAND_DOMAIN, LEGAL_NAME } from "@/lib/brand"
  import { docsUrl } from "@/lib/docs"
  import { languageSlug, localized } from "@/lib/locale-routes"
  import {
    aboutPath,
    cancelContractPath,
    contactPath,
    legalDataProcessorsPath,
    legalImprintPath,
    legalPrivacyPath,
    legalTermsPath,
    newsPath,
    pressPath,
    reportPath,
  } from "@/routes"

  // External uptime/status page (UptimeRobot). Opens in a new tab; nofollow so
  // we don't pass link equity to a third-party host.
  const STATUS_URL = "https://stats.uptimerobot.com/eKNbJ06n2q"

  // `code` is the active public code (a language, or a region code like
  // "de-CH" when a geo overlay is active); URLs only ever carry the
  // language slug.
  const code = $derived($locale ?? "en")
  const slug = $derived(languageSlug(code))
  const pricingHref = $derived(localized("/pricing", code))
  const docsHref = $derived(docsUrl("", code))
  const contactHref = $derived(contactPath({ locale: slug }))
  // Who is behind TOBY. Sits in the Product column because the Support
  // column is where a customer with a problem looks, and this is not that.
  const aboutHref = $derived(aboutPath({ locale: slug }))
  // Logo downloads for press and social-media profiles.
  const pressHref = $derived(pressPath({ locale: slug }))
  // Owners only, but linked for everyone on purpose: a signed-out visitor
  // lands on the sign-in wall, which is also sign-up.
  const newsHref = $derived(newsPath({ locale: slug }))
  // The Legal column lists the terms of service, the privacy policy, the
  // imprint, and the data-processors disclosure (a subset of the /legal hub).
  const termsHref = $derived(legalTermsPath({ locale: slug }))
  const privacyHref = $derived(legalPrivacyPath({ locale: slug }))
  const imprintHref = $derived(legalImprintPath({ locale: slug }))
  const dataProcessorsHref = $derived(legalDataProcessorsPath({ locale: slug }))
  // DSA notice-and-action entry point — must stay easy to find.
  const reportHref = $derived(reportPath({ locale: slug }))
  // §312k BGB Kündigungsschaltfläche. The statute requires it to be
  // permanently available and directly reachable on every page, so it
  // belongs in the footer and must not move behind a login or a menu.
  const cancelHref = $derived(cancelContractPath({ locale: slug }))
  // IHK München's "Pack ma's digital" partner badge. The logo, the link and
  // the sentence are the IHK's required wording for its Oberbayern initiative,
  // so the badge stays German-only rather than going through the locales.
  const showPackmasDigital = $derived(slug === "de")

  // NOTE: keep these to utility classes that already appear elsewhere in the
  // app so Tailwind v4 has them compiled. The grid columns + gap below
  // (grid, gap-8, gap-12, sm:grid-cols-2, lg:grid-cols-4) are all in use on
  // the homepage/Plans.
  const linkClass = "text-sm text-[#706f6c] hover:underline dark:text-[#A1A09A]"
  const headingClass =
    "text-sm font-medium tracking-tight text-[#1b1b18] dark:text-[#EDEDEC]"
</script>

<!-- The divider spans the full viewport (matching the homepage section
     borders); only the content is centered/constrained. -->
<footer class="border-t border-black/[.06] dark:border-white/10">
  <div class="mx-auto flex max-w-6xl flex-col gap-12 px-6 py-12">
    <!-- The brand block is the grid's first column rather than a full-width
         row above it: that is what gives the wordmark, the origin line and the
         social row one left-hand section instead of three stacked strips. It
         keeps its own column on `sm` (2-up) and `lg` (4-up); on a phone it
         simply leads the stack, exactly as the old full-width row did. -->
    <div class="grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
      <div class="flex flex-col items-start gap-4">
        <div class="flex items-center gap-3">
          <TobyIcon size={40} />
          <span class="text-base font-medium tracking-tight">
            {$_("brandSlogan")}
          </span>
        </div>
        <!-- Where TOBY is built. The company details themselves are the
             imprint's job (Legal column) — this is the provenance line, and
             it is the reason the footer names a city at all. -->
        <span
          class="flex items-center gap-2 text-sm text-[#706f6c] dark:text-[#A1A09A]"
        >
          {$_("home.made_in")}
          <!-- Munich, Bavaria, Germany. Inline SVG, not emoji: neither city
               nor state has a flag emoji, and Windows renders flag emoji as
               letters. Munich shows its colours only — the Kindl is the
               city's coat of arms, which needs the city's permission. -->
          <span class="flex items-center gap-1" aria-hidden="true">
            <svg viewBox="0 0 5 3" class="h-3 w-5 rounded-[2px]">
              <rect width="5" height="1.5" fill="#000" />
              <rect y="1.5" width="5" height="1.5" fill="#fc0" />
            </svg>
            <svg
              viewBox="0 0 20 12"
              class="h-3 w-5 rounded-[2px] ring-1 ring-black/10"
            >
              <defs>
                <pattern
                  id="bavaria-lozenges"
                  width="4"
                  height="3"
                  patternUnits="userSpaceOnUse"
                  patternTransform="rotate(-22) skewX(18)"
                >
                  <rect width="4" height="3" fill="#fff" />
                  <path d="M2 0 4 1.5 2 3 0 1.5Z" fill="#0099dd" />
                </pattern>
              </defs>
              <rect width="20" height="12" fill="url(#bavaria-lozenges)" />
            </svg>
            <svg viewBox="0 0 5 3" class="h-3 w-5 rounded-[2px]">
              <rect width="5" height="1" fill="#000" />
              <rect y="1" width="5" height="1" fill="#d00" />
              <rect y="2" width="5" height="1" fill="#ffce00" />
            </svg>
          </span>
        </span>
        <SocialLinks />
      </div>

      <nav class="flex flex-col gap-3">
        <h2 class={headingClass}>{$_("home.footer_product")}</h2>
        <div class="flex flex-col items-start gap-2">
          <a href={pricingHref} use:inertia class={linkClass}>
            {$_("home.pricing_link")}
          </a>
          <a
            href={docsHref}
            target="_blank"
            rel="noopener noreferrer"
            class={linkClass}
          >
            {$_("home.docs_link")}
          </a>
          <a href={aboutHref} use:inertia class={linkClass}>
            {$_("home.about_link")}
          </a>
          <a href={pressHref} use:inertia class={linkClass}>
            {$_("home.press_link")}
          </a>
          <a href={newsHref} use:inertia class={linkClass}>
            {$_("whats_new.title")}
          </a>
        </div>
      </nav>

      <nav class="flex flex-col gap-3">
        <h2 class={headingClass}>{$_("home.footer_support")}</h2>
        <div class="flex flex-col items-start gap-2">
          <a href={contactHref} use:inertia class={linkClass}>
            {$_("home.contact_link")}
          </a>
          <a
            href={STATUS_URL}
            target="_blank"
            rel="noopener noreferrer nofollow"
            class={linkClass}
          >
            {$_("home.status_link")}
          </a>
        </div>
      </nav>

      <nav class="flex flex-col gap-3">
        <h2 class={headingClass}>{$_("home.footer_legal")}</h2>
        <div class="flex flex-col items-start gap-2">
          <a href={termsHref} use:inertia class={linkClass}>
            {$_("home.terms_of_service_link")}
          </a>
          <a href={privacyHref} use:inertia class={linkClass}>
            {$_("home.privacy_link")}
          </a>
          <a href={imprintHref} use:inertia class={linkClass}>
            {$_("home.imprint_link")}
          </a>
          <a href={dataProcessorsHref} use:inertia class={linkClass}>
            {$_("home.data_processors_link")}
          </a>
          <a href={reportHref} use:inertia class={linkClass}>
            {$_("home.report_link")}
          </a>
          <a href={cancelHref} use:inertia class={linkClass}>
            {$_("home.cancel_link")}
          </a>
        </div>
      </nav>
    </div>

    {#if showPackmasDigital}
      <div class="flex flex-col items-start gap-4 sm:flex-row sm:items-center">
        <!-- The PNG is flattened onto white, so it reads in dark mode too. -->
        <a
          href="https://www.packmasdigital.de"
          target="_blank"
          rel="noopener noreferrer"
          class="shrink-0 overflow-hidden rounded-md ring-1 ring-black/[.06] dark:ring-white/10"
        >
          <img
            src="/partners/packmas-digital.png"
            alt="IHK-Initiative Pack ma's digital"
            width="220"
            height="90"
            loading="lazy"
          />
        </a>
        <p class="max-w-xl text-sm text-[#706f6c] dark:text-[#A1A09A]">
          Wir unterstützen die IHK-Initiative Pack ma's digital, um der
          Wirtschaft in Oberbayern bei der Digitalisierung weiter zu helfen und
          die Zukunft des Standorts zu sichern.
        </p>
      </div>
    {/if}

    <div class="flex flex-wrap items-center justify-between gap-4">
      <div class="flex flex-wrap items-center gap-2">
        <LanguageSwitcher plain />
      </div>
      <ThemeSwitcher />
    </div>

    <p class="text-sm text-[#706f6c] dark:text-[#A1A09A]">
      {BRAND_DOMAIN} · {LEGAL_NAME}
    </p>
  </div>
</footer>
